<script setup lang="ts">
import { getToken } from '@josempgon/vue-keycloak'
import type { DataTableHeaders } from '@/plugins/vuetify';
import type MDBAlerte from '@/types/alerte';
import { notifyError, toDate_wd_dd_MMMM_hh_mm } from '@/utils/helpers'

definePage({
  meta: {
    id: 'admn.alertes',
    title: 'Alertes système',
    icon: 'mdi-alert-octagon-outline',
    drawerIndex: 0,
    requiresAuth: true,
    requiresAdmin: true
  },
})

const theme = useTheme()
const storeU = useUtilisateurStore()
const isLoading = ref(false)

const jwtCopied = ref(false)
const jwtToken = ref<string | null>(null)
async function loadAndCopyJwtToken() {
  const token = await getToken()
  if (!token) return
  jwtToken.value = token
  try {
    await navigator.clipboard.writeText(token)
  } catch {
    // Fallback for non-secure contexts
    const el = document.createElement('textarea')
    el.value = token
    el.style.cssText = 'position:fixed;opacity:0;pointer-events:none'
    document.body.appendChild(el)
    el.focus()
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)
  }
  jwtCopied.value = true
  setTimeout(() => { jwtCopied.value = false }, 2000)
}

// ── Administration alerts widget ───────────────────────────
interface SévéritéInfo {
  label: string
  color: string
  icon: string
}

function getSévérité(sévérité: number): SévéritéInfo {
  switch (sévérité) {
    case 1: return { label: 'Info', color: 'info', icon: 'mdi-information-outline' }
    case 2: return { label: 'Avertissement', color: 'warning', icon: 'mdi-alert-outline' }
    case 3: return { label: 'Erreur', color: 'error', icon: 'mdi-alert-circle-outline' }
    case 4: return { label: 'Critique', color: 'error', icon: 'mdi-alert-octagon-outline' }
    default: return { label: 'Debug', color: 'grey', icon: 'mdi-bug-outline' }
  }
}

const alertes = ref([] as MDBAlerte[])
const isLoadingAlertes = ref(false)
const searchAlertes = ref('')

const MESSAGE_MAX_LENGTH = 180
const expandedMessages = ref(new Set<string>())

function isMessageLong(message: string): boolean {
  return (message?.length ?? 0) > MESSAGE_MAX_LENGTH
}

function getMessagePreview(message: string): string {
  return message.slice(0, MESSAGE_MAX_LENGTH).trimEnd() + '…'
}

function toggleMessage(id: string) {
  if (expandedMessages.value.has(id))
    expandedMessages.value.delete(id)
  else
    expandedMessages.value.add(id)
  expandedMessages.value = new Set(expandedMessages.value)
}

const alertesHeaders: DataTableHeaders = [
  { title: 'Date', key: 'formattedCréeLe', width: '160px', sortable: false },
  { title: 'Créé par', key: 'crééPar', width: '140px', sortable: false },
  { title: 'Sévérité', key: 'sévérité', width: '120px', sortable: false },
  { title: 'Type', key: 'typeAlerte', width: '180px', sortable: false },
  { title: 'Message', key: 'message', sortable: false },
  { title: 'Actions', key: 'actions', width: '120px', sortable: false },
]

const formattedAlertes = computed(() => {
  return alertes.value
    .slice()
    .sort((a, b) => new Date(b.crééLe).getTime() - new Date(a.crééLe).getTime())
    .map(alerte => ({
      ...alerte,
      formattedCréeLe: toDate_wd_dd_MMMM_hh_mm(alerte.crééLe),
      message: alerte.message || 'Aucun message',
    }))
})

const storeApp = useAppStore()

async function getAlertes() {
  try {
    isLoadingAlertes.value = true
    const results = await apiProphet.get('api/alertes/systeme')
    alertes.value = (results.data as MDBAlerte[])
      .filter(alerte => alerte.destinataireType === 'Agrimétrie')
    storeApp.adminAlertCount = alertes.value.length
  }
  catch (error) {
    notifyError(error)
  }
  finally {
    isLoadingAlertes.value = false
  }
}

async function confirmer(alerte: MDBAlerte) {
  try {
    const config = { params: { confirmer: true } }
    const result = await apiProphet.put(`api/alertes/${alerte.id}/confirmer`, null, config)
    if (result.status == 200) {
      Notify.success('Alerte confirmée avec succès')
      getAlertes()
    }
  } catch (error) {
    notifyError(error)
  }
}

async function effacer(alerte: MDBAlerte) {
  try {
    const result = await apiProphet.delete(`api/alertes/${alerte.id}`)
    if (result.status == 200) {
      Notify.success('Alerte effacée avec succès')
      getAlertes()
    }
  } catch (error) {
    notifyError(error)
  }
}

const isConfirmingAll = ref(false)
async function confirmerTous() {
  try {
    isConfirmingAll.value = true
    const config = { params: { confirmer: true } }
    await Promise.all(
      alertes.value.map(alerte => apiProphet.put(`api/alertes/${alerte.id}/confirmer`, null, config))
    )
    Notify.success('Toutes les alertes ont été confirmées')
    getAlertes()
  } catch (error) {
    notifyError(error)
  } finally {
    isConfirmingAll.value = false
  }
}

const dialogRevisionsWatch = ref(false)
const revisionChoice = ref('buckets_commandes')
const revisionsHeaders: DataTableHeaders = [
  { title: 'Date', key: 'date', width: '200px' },
  { title: 'Raison', key: 'raison'}
];
const revisions = ref([] as { date: string; raison: string }[])
function openRevisionsWatch() {
  dialogRevisionsWatch.value = true
}
async function loadRevisions() {
  try {
    isLoading.value = true

    let siteCS = storeU.siteSélectionné.lettresDappel

    if (revisionChoice.value === 'cron' || revisionChoice.value === 'cron_prisme') {
      siteCS = 'omni'
    }

    const result = await apiProphet.get(`sites/${siteCS}/revisions/${revisionChoice.value}/raisons`)
    revisions.value = result.data.map((r: string) => {
      const parts = r.split('|')
      return { date: parts[0], raison: parts[1] }
    })
    revisions.value.reverse()
  }
  catch (e) {
    Notify.error(e)
  }
  finally {
    isLoading.value = false
  }
}

onMounted(() => {
  getAlertes()
})
</script>

<template>
  <v-card class="mx-4 my-4 page-card" rounded="xl" :loading="isLoading || isLoadingAlertes">

    <!-- Page header -->
    <div class="page-header px-5 pt-5 pb-3">
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">

        <!-- Left: title -->
        <div class="d-flex align-center" style="gap: 12px;">
          <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
            <v-icon icon="mdi-security" size="26"/>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Administration</div>
            <div class="text-caption text-medium-emphasis">Outils et alertes système</div>
          </div>
        </div>

        <!-- Right: actions -->
        <div class="d-flex align-center flex-wrap" style="gap: 8px;">
          <v-btn
            color="primary" variant="tonal" rounded="pill"
            prepend-icon="mdi-history"
            @click="openRevisionsWatch">
            Revisions Watch
          </v-btn>
          <v-btn
            :color="jwtCopied ? 'success' : 'primary'"
            variant="tonal" rounded="pill"
            :prepend-icon="jwtCopied ? 'mdi-check' : 'mdi-content-copy'"
            @click="loadAndCopyJwtToken">
            {{ jwtCopied ? 'Copié !' : 'Copier JWT' }}
          </v-btn>
        </div>
      </div>
    </div>

    <v-divider/>

    <!-- Content -->
    <div class="pa-4">

      <!-- Administration alerts widget -->
      <v-card variant="outlined" rounded="lg" class="mb-4">
        <v-card-title class="d-flex align-center flex-wrap" style="gap: 10px;">
          <v-avatar color="error" variant="tonal" size="36" rounded="lg">
            <v-icon icon="mdi-alert-octagon-outline" size="20"/>
          </v-avatar>
          <div style="flex: 1;">
            <div class="text-subtitle-1 font-weight-bold">Alertes Administration</div>
            <div class="text-caption text-medium-emphasis">Notifications destinées à l'administration</div>
          </div>
          <v-chip size="small" color="error" variant="tonal" label>
            {{ formattedAlertes.length }}
          </v-chip>
          <v-btn
            v-if="alertes.length > 0"
            color="success" variant="tonal" rounded="lg" size="small"
            prepend-icon="mdi-check-all"
            :loading="isConfirmingAll"
            @click="confirmerTous">
            Tout confirmer
          </v-btn>
          <v-text-field
            v-model="searchAlertes"
            label="Rechercher"
            prepend-inner-icon="mdi-magnify"
            density="compact" variant="outlined" rounded="lg" hide-details clearable
            style="max-width: 260px; min-width: 160px;"/>
        </v-card-title>

        <v-divider/>

        <v-data-table
          :headers="alertesHeaders"
          :items="formattedAlertes"
          :search="searchAlertes"
          :loading="isLoadingAlertes"
          item-value="id"
          :hide-default-header="$vuetify.display.smAndDown"
          no-data-text="Aucune alerte d'administration">

          <template #item.sévérité="{ item }">
            <v-chip
              :color="getSévérité(item.sévérité).color"
              :prepend-icon="getSévérité(item.sévérité).icon"
              size="small" variant="tonal" label>
              {{ getSévérité(item.sévérité).label }}
            </v-chip>
          </template>

          <template #item.message="{ item }">
            <div class="message-cell py-2">
              <div class="message-text" style="white-space: pre-wrap; word-break: break-word;">{{ isMessageLong(item.message) && !expandedMessages.has(item.id) ? getMessagePreview(item.message) : item.message }}</div>
              <v-btn
                v-if="isMessageLong(item.message)"
                size="x-small" color="primary" variant="text" class="mt-1 px-1"
                :append-icon="expandedMessages.has(item.id) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                @click="toggleMessage(item.id)">
                {{ expandedMessages.has(item.id) ? 'Voir moins' : 'Voir plus' }}
              </v-btn>
            </div>
          </template>

          <template #item.actions="{ item }">
            <div class="d-flex align-center" style="gap: 4px;">
              <v-tooltip text="Confirmer" location="top">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-check" color="success"
                    size="small" variant="text"
                    @click="confirmer(item)"/>
                </template>
              </v-tooltip>
              <v-tooltip text="Effacer" location="top">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-delete-outline" color="error"
                    size="small" variant="text"
                    @click="effacer(item)"/>
                </template>
              </v-tooltip>
            </div>
          </template>

          <!-- Mobile card layout -->
          <template v-if="$vuetify.display.smAndDown" #item="{ item }">
            <tr>
              <td class="pa-0">
                <v-card variant="flat" class="alerte-mobile-card pa-3 ma-2" rounded="lg">
                  <div class="d-flex align-center justify-space-between mb-2" style="gap: 8px;">
                    <v-chip
                      :color="getSévérité(item.sévérité).color"
                      :prepend-icon="getSévérité(item.sévérité).icon"
                      size="small" variant="tonal" label>
                      {{ getSévérité(item.sévérité).label }}
                    </v-chip>
                  </div>

                  <div class="text-body-2 font-weight-bold">{{ item.typeAlerte }}</div>
                  <div class="text-caption text-medium-emphasis mb-2">{{ item.formattedCréeLe }}</div>

                  <div class="text-body-2 mb-1" style="white-space: pre-wrap; word-break: break-word;">{{ isMessageLong(item.message) && !expandedMessages.has(item.id) ? getMessagePreview(item.message) : item.message }}</div>
                  <v-btn
                    v-if="isMessageLong(item.message)"
                    size="x-small" color="primary" variant="text" class="mb-2 px-1"
                    :append-icon="expandedMessages.has(item.id) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                    @click="toggleMessage(item.id)">
                    {{ expandedMessages.has(item.id) ? 'Voir moins' : 'Voir plus' }}
                  </v-btn>

                  <div class="d-flex flex-column text-caption text-medium-emphasis mb-2" style="gap: 2px;">
                    <span><v-icon icon="mdi-account-outline" size="14" class="me-1"/>Créé par : {{ item.crééPar }}</span>
                  </div>

                  <v-divider class="my-2"/>

                  <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                    <v-spacer/>
                    <v-btn
                      prepend-icon="mdi-check" color="success"
                      size="small" variant="text"
                      @click="confirmer(item)">
                      Confirmer
                    </v-btn>
                    <v-btn
                      icon="mdi-delete-outline" color="error"
                      size="small" variant="text"
                      @click="effacer(item)"/>
                  </div>
                </v-card>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>

      <!-- JWT token card -->
      <v-card v-if="jwtToken" variant="outlined" rounded="lg" class="pa-2">
        <v-card-subtitle class="mb-1">JWT Token</v-card-subtitle>
        <v-textarea
          :model-value="jwtToken"
          readonly
          auto-grow
          rows="3"
          variant="outlined"
          density="compact"
          hide-details
          style="font-family: monospace; font-size: 0.75rem;"
        />
      </v-card>

    </div>
  </v-card>

  <v-dialog v-model="dialogRevisionsWatch" max-width="90%" :fullscreen="$vuetify.display.smAndDown">
    <v-card style="z-index: -1">
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h5 text-medium-emphasis ps-2">Revisions Watch</span>
        <v-btn icon="mdi-close" variant="text" @click="dialogRevisionsWatch = false"/>
      </v-card-title>

      <v-divider class="mb-4"/>

      <v-card-text>
        <v-container>
          <v-row>
            <v-col >
              <v-select
                v-model="revisionChoice"
                label="Tables à surveiller"
                :items="['cron', 'cron_prisme', 'alertes', 'champs', 'cultures', 'semis', 'buckets', 'commandes', 'buckets_commandes', 'degrejours', 'ogn_modeles', 'vegco_requetes']"
              />
            </v-col>
            <v-col cols="3">
              <v-btn @click="loadRevisions"><v-icon>mdi-reload</v-icon> Load Revisions</v-btn>
            </v-col>
          </v-row>
        </v-container>
        <v-data-table
          :headers="revisionsHeaders" :items="revisions" :loading="isLoading"
          :items-per-page="-1"/>
      </v-card-text>

      <v-divider class="mt-2"/>

      <v-card-actions class="my-2 d-flex justify-end mr-2">
        <v-btn color="primary" variant="flat" @click="dialogRevisionsWatch = false">OK</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
  background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

/* ── Message cell ────────────────────────── */
.message-text {
  font-size: 0.8125rem;
  line-height: 1.4;
}

/* ── Mobile alert card ───────────────────── */
.alerte-mobile-card {
  border: 1px solid v-bind('theme.global.name.value === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"');
  background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.18)" : "rgba(var(--v-theme-primary), 0.02)"');
}
</style>
